// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
import { AlertCircle, FileText, Search, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useSidebar } from "@/components/ui/sidebar";
import { SortIcon } from "@/components/ui/sort-icon";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { type AppStatus, useApps } from "@/hooks/api/use-apps";
import { usePageTitle } from "@/hooks/use-page-title";
import { formatDate } from "@/lib/date-formatting";
import type { SortDirection } from "@/types/table-column";

type SortColumn = "name" | "updatedAt";

/**
 * Display status badge with appropriate styling.
 * Maps API status values to user-friendly labels.
 */
function getStatusBadge(status: AppStatus) {
  const config: Record<
    AppStatus,
    {
      label: string;
      variant: "secondary" | "team-emerald" | "team-amber" | "destructive";
    }
  > = {
    draft: { label: "Draft", variant: "secondary" },
    active: { label: "Active", variant: "team-emerald" },
    disabled: { label: "Disabled", variant: "destructive" },
    archived: { label: "Archived", variant: "team-amber" },
  };

  const { label, variant } = config[status] ?? {
    label: status,
    variant: "secondary",
  };
  return <Badge variant={variant}>{label}</Badge>;
}

export default function AppListView() {
  usePageTitle("Apps");
  const navigate = useNavigate();
  const { state, isMobile } = useSidebar();
  const [searchTerm, setSearchTerm] = useState("");
  const [sortColumn, setSortColumn] = useState<SortColumn>("updatedAt");
  const [sortDirection, setSortDirection] =
    useState<NonNullable<SortDirection>>("desc");

  // Fetch apps from API - by default returns active apps
  const { data: apps = [], isLoading, error } = useApps();

  // Transform API data to table-friendly format
  const tableApps = useMemo(() => {
    return apps.map((app) => ({
      id: app.id,
      name: app.display_name,
      description: app.description ?? "",
      status: app.status,
      updatedAt: app.updated_at,
    }));
  }, [apps]);

  const filteredApps = tableApps.filter(
    (app) =>
      app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.description.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const sortedApps = useMemo(() => {
    return [...filteredApps].sort((a, b) => {
      const aValue = sortColumn === "name" ? a.name : a.updatedAt;
      const bValue = sortColumn === "name" ? b.name : b.updatedAt;
      const comparison = aValue.localeCompare(bValue);
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [filteredApps, sortColumn, sortDirection]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const handleRowClick = (event: React.MouseEvent, appId: string) => {
    const url = `/apps/${appId}`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };

  return (
    <div className="flex flex-col h-full space-y-4">
      <div className="shrink-0">
        <div className="flex flex-col space-y-4 pt-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0 sm:space-x-4">
          <h1>Apps</h1>
        </div>
      </div>

      <div className="flex-1 min-h-0">
        <Card className="h-full relative">
          <div className="p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Filter by app name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-[250px] border-0 border-b border-border shadow-none rounded-none pl-7 pr-8 h-10 placeholder:text-muted-foreground focus:ring-0 focus-visible:ring-0 focus:border-muted-foreground transition-colors bg-transparent hover:border-muted-foreground"
                />
                {searchTerm && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-1/2 -translate-y-1/2 h-6 w-6 p-0 hover:bg-transparent"
                    onClick={() => setSearchTerm("")}
                  >
                    <X className="h-4 w-4 text-muted-foreground hover:text-foreground/80" />
                  </Button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="text-sm text-muted-foreground text-right">
                {isLoading
                  ? "Loading..."
                  : error
                    ? "Error loading apps"
                    : sortedApps.length > 0
                      ? `${sortedApps.length.toLocaleString()} ${
                          sortedApps.length === 1 ? "app" : "apps"
                        } found`
                      : "No apps found"}
              </div>
            </div>
          </div>

          <div
            className="rounded-md border max-h-[calc(100vh-11rem)] overflow-auto"
            style={{
              maxWidth: isMobile
                ? "calc(100vw - 2rem)"
                : state === "collapsed"
                  ? "calc(100vw - var(--sidebar-width-icon) - 2rem)"
                  : "calc(100vw - var(--sidebar-width) - 2rem)",
              minWidth: "640px",
            }}
          >
            <Table className="w-full table-fixed">
              {isLoading ? (
                <>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead style={{ width: 220 }}>
                        <div className="h-4 w-16 bg-muted rounded animate-pulse" />
                      </TableHead>
                      <TableHead style={{ width: 350 }}>
                        <div className="h-4 w-24 bg-muted rounded animate-pulse" />
                      </TableHead>
                      <TableHead style={{ width: 100 }}>
                        <div className="h-4 w-16 bg-muted rounded animate-pulse" />
                      </TableHead>
                      <TableHead style={{ width: 130 }}>
                        <div className="h-4 w-20 bg-muted rounded animate-pulse" />
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody className="bg-card">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <div className="h-4 w-32 bg-muted rounded animate-pulse" />
                        </TableCell>
                        <TableCell>
                          <div className="h-4 w-48 bg-muted rounded animate-pulse" />
                        </TableCell>
                        <TableCell>
                          <div className="h-5 w-16 bg-muted rounded-full animate-pulse" />
                        </TableCell>
                        <TableCell>
                          <div className="h-4 w-20 bg-muted rounded animate-pulse" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </>
              ) : (
                <>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead
                        className="text-xs font-medium cursor-pointer hover:bg-muted/80 transition-colors group"
                        style={{ width: 220 }}
                        onClick={() => handleSort("name")}
                      >
                        <div className="flex items-center gap-1">
                          Name
                          <SortIcon
                            isActive={sortColumn === "name"}
                            direction={sortDirection}
                          />
                        </div>
                      </TableHead>
                      <TableHead
                        className="text-xs font-medium"
                        style={{ width: 350 }}
                      >
                        Description
                      </TableHead>
                      <TableHead
                        className="text-xs font-medium"
                        style={{ width: 100 }}
                      >
                        Status
                      </TableHead>
                      <TableHead
                        className="text-xs font-medium cursor-pointer hover:bg-muted/80 transition-colors group"
                        style={{ width: 130 }}
                        onClick={() => handleSort("updatedAt")}
                      >
                        <div className="flex items-center gap-1">
                          Last Updated
                          <SortIcon
                            isActive={sortColumn === "updatedAt"}
                            direction={sortDirection}
                          />
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody className="bg-card">
                    {error ? (
                      <TableRow>
                        <TableCell colSpan={4} className="h-24 text-center">
                          <div className="flex flex-col items-center justify-center gap-2 text-destructive">
                            <AlertCircle className="h-6 w-6" />
                            <span className="text-sm font-medium">
                              {error.message || "Failed to load apps"}
                            </span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : sortedApps.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} className="h-48 text-center">
                          <div className="flex flex-col items-center justify-center gap-3">
                            <div className="rounded-full bg-muted p-4">
                              <FileText className="h-8 w-8 text-muted-foreground" />
                            </div>
                            <div className="space-y-1">
                              <p className="text-sm font-medium text-foreground">
                                {searchTerm
                                  ? "No matching apps"
                                  : "No apps yet"}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {searchTerm
                                  ? "Try adjusting your search terms"
                                  : "Apps will appear here once they're created"}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedApps.map((app) => (
                        <TableRow
                          key={app.id}
                          className="hover:bg-muted/50 transition-colors cursor-pointer"
                          onClick={(e) => handleRowClick(e, app.id)}
                        >
                          <TableCell className="text-xs">
                            <span className="font-medium">{app.name}</span>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {app.description}
                          </TableCell>
                          <TableCell className="text-xs">
                            {getStatusBadge(app.status)}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {formatDate(app.updatedAt)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </>
              )}
            </Table>
          </div>
        </Card>
      </div>
    </div>
  );
}
